import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Send, Bot, User } from 'lucide-react';
import { Message } from '../types';
import './ChatInterface.css';

interface ChatInterfaceProps {
  familyId: string | null;
  apiBase: string;
  onMessageSent?: (message: string) => void;
  wsClient: any;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  familyId,
  apiBase,
  onMessageSent,
  wsClient
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadChatHistory = useCallback(async () => {
    if (!familyId) return;

    try {
      const response = await fetch(`${apiBase}/chat/history/${familyId}`);
      if (response.ok) {
        const data = await response.json();
        
        const formattedMessages: Message[] = data.messages.map((msg: any) => ({
          _id: msg.id || msg.timestamp,
          text: msg.content,
          createdAt: new Date(msg.timestamp),
          user: {
            _id: msg.role === 'user' ? 'current-user' : 'assistant',
            name: msg.role === 'user' ? 'You' : 'AI Assistant',
          },
          role: msg.role,
        }));

        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  }, [familyId, apiBase]);

  useEffect(() => {
    if (familyId) {
      loadChatHistory();
    } else {
      setMessages([]);
    }
  }, [familyId, loadChatHistory]);

  useEffect(() => {
    const handleChatMessage = (data: any) => {
      if (data.family_id === familyId) {
        const newMessage: Message = {
          _id: data.id || Date.now().toString(),
          text: data.content,
          createdAt: new Date(data.timestamp || Date.now()),
          user: {
            _id: data.role === 'user' ? 'current-user' : 'assistant',
            name: data.role === 'user' ? 'You' : 'AI Assistant',
          },
          role: data.role,
        };
        
        setMessages(prev => [...prev, newMessage]);
        
        if (data.role === 'assistant') {
          setIsTyping(false);
        }
      }
    };

    wsClient.on('chat_message', handleChatMessage);

    return () => {
      wsClient.off('chat_message', handleChatMessage);
    };
  }, [familyId, wsClient]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !familyId) return;

    const messageText = inputValue.trim();
    setInputValue('');
    setIsTyping(true);

    // Dispatch chat debug event for request
    window.dispatchEvent(new CustomEvent('chatDebug', {
      detail: {
        type: 'chat_request',
        payload: {
          family_id: familyId,
          message: messageText,
          timestamp: new Date().toISOString()
        }
      }
    }));

    const userMessage: Message = {
      _id: Date.now().toString(),
      text: messageText,
      createdAt: new Date(),
      user: {
        _id: 'current-user',
        name: 'You',
      },
      role: 'user',
    };

    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await fetch(`${apiBase}/chat/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: messageText,
          family_id: familyId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Dispatch chat debug event for response
        window.dispatchEvent(new CustomEvent('chatDebug', {
          detail: {
            type: 'chat_response',
            payload: {
              family_id: familyId,
              response: data.response,
              tokens_used: data.tokens_used || 0,
              cost: data.cost || 0,
              cache_used: data.cache_used || false,
              model: data.model || 'gpt-3.5-turbo',
              timestamp: new Date().toISOString()
            }
          }
        }));
        
        const assistantMessage: Message = {
          _id: data.id || (Date.now() + 1).toString(),
          text: data.response,
          createdAt: new Date(),
          user: {
            _id: 'assistant',
            name: 'AI Assistant',
          },
          role: 'assistant',
        };

        setMessages(prev => [...prev, assistantMessage]);
        setIsTyping(false);

        if (onMessageSent) {
          onMessageSent(messageText);
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!familyId) {
    return (
      <div className="chat-placeholder">
        <div className="chat-placeholder-content">
          <Bot className="chat-placeholder-icon" />
          <p className="chat-placeholder-text">Select a family to start chatting</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-interface">
      {/* Chat Header */}
      <div className="chat-header">
        <div className="chat-header-content">
          <div className="chat-avatar">
            <Bot size={20} />
          </div>
          <div className="chat-header-info">
            <h3>AI Assistant</h3>
            <p>Always here to help</p>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="chat-empty">
            <Bot size={48} />
            <p>No messages yet. Start a conversation!</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message._id}
              className={`message-row ${message.role}`}
            >
              {message.role === 'assistant' && (
                <div className="message-avatar">
                  <Bot size={16} />
                </div>
              )}
              
              <div className={`message-bubble ${message.role}`}>
                <p className="message-text">{message.text}</p>
                <p className="message-time">
                  {new Date(message.createdAt).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </p>
              </div>

              {message.role === 'user' && (
                <div className="message-avatar">
                  <User size={16} />
                </div>
              )}
            </div>
          ))
        )}
        
        {isTyping && (
          <div className="typing-indicator">
            <div className="message-avatar">
              <Bot size={16} />
            </div>
            <div className="typing-dots">
              <div className="typing-dot" />
              <div className="typing-dot" />
              <div className="typing-dot" />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="chat-input-area">
        <div className="chat-input-container">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className="chat-textarea"
            rows={1}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim()}
            className="chat-send-button"
            aria-label="Send message"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};
